import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";

import { CalendarIcon, LightbulbIcon, PlusIcon } from "lucide-react";

import { formatDate } from "simplydo/core/dates";
import type { OpenAPI } from "simplydo/interfaces";
import { cx, styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { Container } from "simplydo/react/ui/container";
import {
  Dropdown,
  DropdownItemLink,
  DropdownMenu,
} from "simplydo/react/ui/dropdown";
import { H1, H5 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";
import {
  Placeholder,
  PlaceholderImage,
  PlaceholderLine,
  PlaceholderParagraph,
} from "simplydo/react/ui/placeholder";

import { useAppSelector } from "store";
import util from "utils/utils";

import { AnonymousChip, ProfileChip } from "components/lib/Chips";
import Tag, { TagContainer } from "components/lib/Tag";
import { Banner } from "components/lib/UI";

import type { ChallengeHeader as ChallengeHeaderType } from "../types";
import InnoIntelSearchAction from "./InnoIntelSearchAction";

const StyledCoverImage = styled.div<{ $image: string }>`
  border-radius: 3px;
  min-width: ${({ theme }) => (theme.sizes.isMobile ? "100px" : "200px")};
  max-height: ${({ theme }) => (theme.sizes.isMobile ? "100px" : "200px")};
  max-width: ${({ theme }) => (theme.sizes.isMobile ? "100px" : "200px")};
  min-height: ${({ theme }) => (theme.sizes.isMobile ? "100px" : "200px")};
  border: 3px solid white;
  margin-right: 15px;
  box-shadow: 0px 10px 10px rgba(0, 0, 0, 0.3);
  background-size: cover;
  background-position: center center;
  background-image: ${({ $image }) => ($image ? `url(${$image})` : "none")};
`;

export const StyledContainer = styled(Container)`
  &.is-computer {
    padding: 60px 0px 30px;
    &.is-fluid {
      padding: 1rem 3rem;
    }
  }
  &.is-mobile {
    padding: 10px 0;
  }
`;

// Also used in group header
export const HeaderContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? "column" : "row")};
  justify-content: space-between;
  align-items: ${({ theme }) =>
    theme.sizes.isMobile ? "flex-start" : "flex-end"};
  &.is-preview {
    padding: 0 2rem;
  }
  margin-top: 3px;
`;

export const HeaderInfo = styled.div`
  display: flex;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? "column" : "row")};
  align-items: ${({ theme }) =>
    theme.sizes.isMobile ? "center" : "flex-start"};
  &.is-mobile {
    margin-top: 5px;
  }
  width: 100%;
  h1,
  h5,
  span:not(.dropdown .text) {
    margin: 0;
    color: white;
  }
  .titles {
    margin-bottom: 1rem;
    .meta {
      display: block;
      font-size: 1.1em;
      margin-bottom: 2.5px;
    }
  }
  &.has-user .owner {
    margin-bottom: 1rem;
  }
  .owner {
    display: flex;
    flex-direction: row;
    align-items: center;
    color: white;
  }
  .info {
    display: flex;
    flex-direction: column;
    width: 100%;
  }
  .sector {
    text-transform: uppercase;
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  min-width: 200px;
  flex-direction: row;
  justify-content: flex-end;
  .ui.button {
    &:not(:last-of-type) {
      margin-right: 0.5rem;
    }
  }
  &.is-mobile {
    margin-bottom: 1rem;
  }
`;

type ChallengeHeaderProps = {
  challenge?: ChallengeHeaderType;
  myIdeas: OpenAPI.Schemas["Idea"][];
  newIdea: () => void;
  isPreview?: boolean;
  isIdeasPage: boolean;
  redirectToLogin: () => void;
  loading: boolean;
};

const ChallengeHeader = ({
  challenge,
  myIdeas,
  newIdea,
  isPreview = false,
  isIdeasPage,
  redirectToLogin,
  loading,
}: ChallengeHeaderProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);
  const canMakeIdea = util.canCreateIdea(user, challenge);

  const myUnsubmittedIdeas = useMemo(
    () => myIdeas?.filter((idea) => !idea.isSubmitted),
    [myIdeas],
  );
  return (
    <Banner
      marginless
      image={
        challenge ? util.getThumbnail(challenge, "bannerImage", "2560x400") : ""
      }
    >
      <StyledContainer
        fluid={isIdeasPage}
        className={cx(
          theme.sizes.isComputer ? "is-computer" : "is-mobile",
          isIdeasPage && "is-fluid",
        )}
      >
        {loading ? (
          <Placeholder>
            <PlaceholderImage square style={{ width: 200, height: 200 }} />
            <PlaceholderParagraph>
              <PlaceholderLine length="medium" style={{ height: 50 }} />
              <PlaceholderLine length="short" style={{ height: 30 }} />
              <PlaceholderLine length="short" style={{ height: 20 }} />
            </PlaceholderParagraph>
          </Placeholder>
        ) : challenge ? (
          <>
            <div className="overlay" />
            <div className="overlay-body">
              <TagContainer>
                {challenge.ownerTags?.map(
                  (
                    tag: NonNullable<ChallengeHeaderType["ownerTags"]>[number],
                  ) => (
                    <Tag key={tag._id} tag={tag} />
                  ),
                )}
              </TagContainer>
              <HeaderContainer className={isPreview ? "is-preview" : undefined}>
                <HeaderInfo
                  className={cx(
                    theme.sizes.isMobile && "is-mobile",
                    user && "has-user",
                  )}
                >
                  {challenge.coverImageUrl && (
                    <StyledCoverImage
                      $image={util.getThumbnail(
                        challenge,
                        "coverImage",
                        "500x500",
                      )}
                    />
                  )}
                  <div className="info">
                    <div className="titles">
                      <H1
                        style={{ marginBottom: 3 }}
                        className="challenge-tour-step-1"
                      >
                        {challenge?.name}
                      </H1>
                      {challenge.tagline ? (
                        <span style={{ marginBottom: 5 }} className="meta">
                          {challenge.tagline}
                        </span>
                      ) : null}
                      {challenge.ownerOrganisation ? (
                        <H5>
                          {" "}
                          {t("challenge.managedBy")}{" "}
                          {challenge.ownerOrganisation.name}
                        </H5>
                      ) : null}
                    </div>
                    <div>
                      <div className="owner">
                        <span className="posted-by" style={{ marginRight: 5 }}>
                          {t("challenge.postedBy")}
                        </span>
                        {challenge.postedByType === "anonymous" ? (
                          <AnonymousChip />
                        ) : null}
                        {challenge.postedByType === "organisation" ||
                        challenge.postedByType === "profile" ? (
                          <ProfileChip
                            profile={challenge.postedBy}
                            inverted
                            forType={challenge.postedByType}
                          />
                        ) : null}
                      </div>
                      {!user && (
                        <div style={{ marginBottom: "2rem" }}>
                          {!challenge.stage && (
                            <Label color="yellow" style={{ marginRight: 10 }}>
                              {t("challenge.stages.draft.title")}
                            </Label>
                          )}
                          {challenge.stage === "published" && (
                            <Label color="green" style={{ marginRight: 10 }}>
                              {t("challenge.stages.published.title")}
                            </Label>
                          )}
                          {challenge.stage === "closed" && (
                            <Label color="red" style={{ marginRight: 10 }}>
                              {t("generic.closed")}
                            </Label>
                          )}
                        </div>
                      )}
                      {challenge.deadline ? (
                        <span>
                          <Icon icon={CalendarIcon} />
                          {t("challenge.deadline")}:{" "}
                          {formatDate(challenge.deadline, "HH:mm")} on{" "}
                          {formatDate(challenge.deadline, "dd/MM/yyyy")}
                        </span>
                      ) : null}
                    </div>
                    <HeaderActions
                      className={theme.sizes.isMobile ? "is-mobile" : undefined}
                    >
                      <InnoIntelSearchAction challenge={challenge} />
                      {(canMakeIdea || !user) &&
                      (challenge.stage === "published" ||
                        util.canManageChallenge(user, challenge) ||
                        util.hasPermission(
                          user,
                          "challenge.editSettings",
                          challenge?._id,
                        )) ? (
                        <Button
                          size={theme.sizes.isMobile ? "small" : undefined}
                          onClick={
                            canMakeIdea ? newIdea : () => redirectToLogin()
                          }
                          color={
                            myUnsubmittedIdeas?.length === 0
                              ? "primary"
                              : undefined
                          }
                        >
                          <Icon icon={PlusIcon} />
                          {challenge?.buttonText
                            ? challenge.buttonText
                            : t("challenge.ideas.createYourIdea")}
                        </Button>
                      ) : null}
                      {user && myUnsubmittedIdeas?.length > 0 && (
                        <React.Fragment>
                          {myUnsubmittedIdeas?.length === 1 && (
                            <ButtonLink
                              size={theme.sizes.isMobile ? "small" : undefined}
                              color="primary"
                              to={`/ideas/${myUnsubmittedIdeas[0]?._id}`}
                            >
                              <Icon icon={LightbulbIcon} />
                              {t("challenge.continueIdea")}
                            </ButtonLink>
                          )}
                          {myUnsubmittedIdeas?.length > 1 && (
                            <Dropdown
                              direction="left"
                              upward
                              trigger={
                                <Button
                                  size={
                                    theme.sizes.isMobile ? "small" : undefined
                                  }
                                  color="primary"
                                >
                                  <Icon icon={LightbulbIcon} />
                                  {t("challenge.continueIdea")}
                                </Button>
                              }
                            >
                              <DropdownMenu>
                                {myUnsubmittedIdeas.map((i) => (
                                  <DropdownItemLink
                                    key={i._id}
                                    to={`/ideas/${i._id}`}
                                  >
                                    {i.name}
                                  </DropdownItemLink>
                                ))}
                              </DropdownMenu>
                            </Dropdown>
                          )}
                        </React.Fragment>
                      )}
                    </HeaderActions>
                  </div>
                </HeaderInfo>
              </HeaderContainer>
            </div>
          </>
        ) : null}
      </StyledContainer>
    </Banner>
  );
};

export default ChallengeHeader;
